import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components";
import { PageHeader } from "../../../components/common";
import { useTranslation } from "react-i18next";
import EGiftCardTable from "../components/EGiftCardTable";
import type {
  EGiftCardListItemResponse,
  DeleteEGiftCardInfoResponse,
} from "../types/eGiftCard.types";
import { Row } from "../../../components/row";
import ShopIconWhite from "../../../assets/icons/shop-small-white.svg";
import EGiftCardModals from "../components/EGiftCardModals";
import EGiftCardStatistics from "../components/EGiftCardStatistics";
import { useEGiftCardActions } from "../hooks/useEGiftCardActions";
import { useEGiftCardList } from "../hooks/useEGiftCardList";
import { useEGiftCardStatistics } from "../hooks/useEGiftCardStatistics";
import { getEGiftCardTotalPages } from "../utils/eGiftCardPagination";
import { RefreshCw } from "lucide-react";
import { useToast } from "../../../components";

export default function EGiftCardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const {
    eGiftCards,
    loading,
    error,
    filter,
    setFilter,
    pagination,
    setPagination,
    fetchEGiftCards,
  } = useEGiftCardList();
  const { statistics, refreshStatistics } = useEGiftCardStatistics();
  const { fetchDeleteEGiftCardInfo, handleRecallEGiftCard } = useEGiftCardActions();
  const [restockEGiftCardId, setRestockEGiftCardId] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    data: DeleteEGiftCardInfoResponse | null;
    loading: boolean;
  }>({ open: false, data: null, loading: false });

  useEffect(() => {
    void refreshStatistics();
  }, [refreshStatistics]);

  const refreshAll = async () => {
    await Promise.all([fetchEGiftCards(), refreshStatistics()]);
  };

  const totalPages = getEGiftCardTotalPages(pagination.total, pagination.size);

  const handlePageChange = (newPage: number) => {
    setPagination({ ...pagination, index: newPage });
  };

  const handleCreate = () => {
    // Navigate to the new Create Brand Gift Card page
    navigate("/e-gift-card/create");
  };

  const handleEdit = async (eGiftCard: EGiftCardListItemResponse) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Navigate to edit page for brand gift cards
    navigate(`/e-gift-card/${eGiftCard.id}/edit`);
  };

  const handleRowClick = (eGiftCard: EGiftCardListItemResponse) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(`/e-gift-card/${eGiftCard.id}/edit`);
  };

  const handleDelete = async (eGiftCard: EGiftCardListItemResponse) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setDeleteModal({ open: true, data: null, loading: true });
    const deleteInfo = await fetchDeleteEGiftCardInfo(eGiftCard.id);
    if (deleteInfo) {
      setDeleteModal({ open: true, data: deleteInfo, loading: false });
    } else {
      setDeleteModal({ open: false, data: null, loading: false });
      addToast({
        type: "error",
        title: "Error!",
        message: "Failed to load e-gift-card delete information",
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.data) return;
    setDeleteModal((current) => ({ ...current, loading: true }));
    const result = await handleRecallEGiftCard(deleteModal.data.coupon_id);
    if (!result) {
      setDeleteModal((current) => ({ ...current, loading: false }));
      addToast({ type: "error", title: "Error!", message: "E-gift-card recall failed." });
      return;
    }
    addToast({
      type: "success",
      title: "Success!",
      message: result.message || "E-gift card has been recalled successfully",
    });
    setDeleteModal({ open: false, data: null, loading: false });
    await refreshAll();
  };

  const handleRestock = async (eGiftCard: EGiftCardListItemResponse) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setRestockEGiftCardId(eGiftCard.id);
  };

  // Helper function to check if any filter is active
  const hasAnyActiveFilter = () => {
    return Object.values(filter).some((value) => {
      if (typeof value === "string") {
        return value && value.trim() !== "";
      }
      if (typeof value === "object" && value !== null) {
        return Object.keys(value).length > 0;
      }
      return false;
    });
  };

  const handleRefreshAll = async () => {
    if (hasAnyActiveFilter()) {
      setFilter({});
      await refreshStatistics();
      return;
    }
    await refreshAll();
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title="Reward List"
        subtitle={t("eGiftCard.subtitle")}
        sticky
        actions={
          <Button
            className="rounded-lg"
            onClick={handleCreate}
          >
            <img
              src={ShopIconWhite}
              alt="Create Brand Gift Card"
              className="inline-block mr-1"
            />
            Create Brand Gift Card
          </Button>
        }
      />

      <div className="px-6 py-6">
        {/* Summary Cards */}
        <EGiftCardStatistics statistics={statistics} />

        {/* Page Header + Refresh button */}
        <Row className="mb-6 justify-between">
          <span className="text-2xl font-semibold text-gray-900 dark:text-white">Reward List</span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshAll}
            disabled={Boolean(loading)}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            {hasAnyActiveFilter() ? t("common.clear") : t("ui.refresh")}
          </Button>
        </Row>

        {/* Table */}
        <EGiftCardTable
          loading={loading}
          error={error}
          eGiftCards={eGiftCards}
          filter={filter}
          setFilter={setFilter}
          onRetry={() => {
            void fetchEGiftCards();
          }}
          onRestock={handleRestock}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onRowClick={handleRowClick}
          page={pagination.index}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />

        {/* Modals */}
        <EGiftCardModals
          restockEGiftCardId={restockEGiftCardId}
          onCloseRestock={() => setRestockEGiftCardId(null)}
          onRestockSuccess={async () => {
            await Promise.all([fetchEGiftCards(), refreshStatistics()]);
          }}
          deleteModal={deleteModal}
          onCloseDelete={() => setDeleteModal({ open: false, data: null, loading: false })}
          onConfirmDelete={() => {
            void handleConfirmDelete();
          }}
        />
      </div>
    </div>
  );
}
